/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive multi-step modal — see project guidelines. */
import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, GraduationCap, Building2, ArrowLeft, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitApplication, uploadResume } from "@/lib/api";

const EXPERIENCE = [
    "Event Production",
    "Promotion",
    "Canva",
    "CapCut",
    "Music Production",
    "Music Performance",
    "Logic Pro",
    "GarageBand",
    "Splice",
    "Suno",
];

const initialInstructor = {
    path: "instructor",
    full_name: "",
    age: "",
    email: "",
    phone: "",
    city: "",
    school: "",
    experience: [],
    resume_url: "",
    why_join: "",
};

const initialSchool = {
    path: "school",
    full_name: "",
    email: "",
    phone: "",
    city: "",
    school_name: "",
    district: "",
    position: "",
    message: "",
};

export const ApplyModal = ({ open, onClose }) => {
    const [path, setPath] = useState(null); // null | "instructor" | "school"
    const [data, setData] = useState(initialInstructor);
    const [submitting, setSubmitting] = useState(false);
    const [resumeFile, setResumeFile] = useState(null); // { name, url, size }
    const [uploading, setUploading] = useState(false);

    const reset = () => {
        setPath(null);
        setData(initialInstructor);
        setSubmitting(false);
        setResumeFile(null);
        setUploading(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const selectPath = (p) => {
        setPath(p);
        setData(p === "instructor" ? initialInstructor : initialSchool);
    };

    const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

    const onResumeSelect = async (file) => {
        if (!file) {
            setResumeFile(null);
            setData((d) => ({ ...d, resume_url: "" }));
            return;
        }
        setUploading(true);
        try {
            const res = await uploadResume(file);
            const url = res.data.url;
            setResumeFile({ name: file.name, size: file.size, url });
            setData((d) => ({ ...d, resume_url: url }));
            toast.success("Resume uploaded.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ?? "Resume upload failed.";
            toast.error(msg);
            setResumeFile(null);
            setData((d) => ({ ...d, resume_url: "" }));
        } finally {
            setUploading(false);
        }
    };

    const toggleExperience = (tag) => {
        setData((d) => ({
            ...d,
            experience: d.experience.includes(tag)
                ? d.experience.filter((t) => t !== tag)
                : [...d.experience, tag],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...data };
            if (payload.age) payload.age = parseInt(payload.age, 10);
            await submitApplication(payload);
            toast.success(
                path === "instructor"
                    ? "Application received! Our team will be in touch within 3 business days."
                    : "Inquiry received! We'll reach out to bring YMP to your school.",
            );
            handleClose();
        } catch (err) {
            toast.error("Submission failed. Please check your details.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center p-4 py-8 sm:py-12 overflow-y-auto"
                    onClick={handleClose}
                    data-testid="apply-modal-overlay"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl my-12 bg-[#0A0A0A] border border-[#D4AF37]/40 gold-glow"
                        data-testid="apply-modal"
                    >
                        <button
                            onClick={handleClose}
                            data-testid="apply-modal-close"
                            className="absolute top-5 right-5 h-10 w-10 border border-white/15 grid place-items-center text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] z-10"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>

                        {!path && <Chooser onPick={selectPath} />}

                        {path === "instructor" && (
                            <InstructorForm
                                data={data}
                                onChange={onChange}
                                toggleExperience={toggleExperience}
                                onBack={() => setPath(null)}
                                onSubmit={submit}
                                submitting={submitting}
                                resumeFile={resumeFile}
                                uploading={uploading}
                                onResumeSelect={onResumeSelect}
                            />
                        )}

                        {path === "school" && (
                            <SchoolForm
                                data={data}
                                onChange={onChange}
                                onBack={() => setPath(null)}
                                onSubmit={submit}
                                submitting={submitting}
                            />
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Chooser = ({ onPick }) => (
    <div className="p-8 md:p-12">
        <div className="flex items-center gap-3 mb-2">
            <Sparkles size={16} className="text-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                Apply To The Movement
            </span>
        </div>
        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[1] text-white">
            Choose Your{" "}
            <span className="gold-text-gradient">Lane</span>
        </h3>
        <p className="mt-3 text-white/60 text-sm">
            Two ways to plug into the Young Mogul ecosystem. Pick the one
            that fits.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <PathCard
                icon={GraduationCap}
                title="Instructor"
                tooltip="Apply to become a Young Mogul Mentor"
                blurb="Join the team — mentor, teach, and build the next generation of moguls."
                onClick={() => onPick("instructor")}
                testId="apply-path-instructor"
            />
            <PathCard
                icon={Building2}
                title="School"
                tooltip="Apply to bring the 9-week Young Mogul Program Accelerator to your school"
                blurb="Bring YMP to your campus — partner with us to launch a cohort at your school."
                onClick={() => onPick("school")}
                testId="apply-path-school"
            />
        </div>
    </div>
);

const PathCard = ({ icon: Icon, title, tooltip, blurb, onClick, testId }) => (
    <button
        onClick={onClick}
        title={tooltip}
        data-testid={testId}
        className="group relative text-left border border-white/10 hover:border-[#D4AF37] hover:gold-glow bg-[#0F0F0F] p-6 transition-all duration-500 overflow-hidden"
    >
        <Icon className="text-[#D4AF37]" size={36} />
        <h4 className="mt-5 font-display text-2xl uppercase tracking-tight text-white">
            {title}
        </h4>
        <p className="mt-2 text-xs text-white/55 leading-relaxed">{blurb}</p>

        {/* Tooltip on hover (works on mouse and touch via title fallback) */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-[#D4AF37] text-black px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold">
            {tooltip}
        </div>
    </button>
);

const InstructorForm = ({ data, onChange, toggleExperience, onBack, onSubmit, submitting, resumeFile, uploading, onResumeSelect }) => (
    <div className="p-8 md:p-12">
        <BackButton onClick={onBack} />

        <div className="flex items-center gap-3 mb-2 mt-4">
            <GraduationCap size={16} className="text-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                Instructor Application
            </span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-[1] text-white">
            Join The{" "}
            <span className="gold-text-gradient">Young Mogul Team</span>
        </h3>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="apply-form-instructor">
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" name="full_name" value={data.full_name} onChange={onChange} required testId="apply-name" />
                <Field label="Email" name="email" type="email" value={data.email} onChange={onChange} required testId="apply-email" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Phone" name="phone" value={data.phone} onChange={onChange} required testId="apply-phone" />
                <Field label="City" name="city" value={data.city} onChange={onChange} required testId="apply-city" />
                <Field label="Age" name="age" type="number" min="16" value={data.age} onChange={onChange} testId="apply-age" />
            </div>
            <Field label="School / Org Affiliation" name="school" value={data.school} onChange={onChange} testId="apply-school" />

            <ResumeUpload
                file={resumeFile}
                uploading={uploading}
                onSelect={onResumeSelect}
            />

            <div>
                <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                    Experience
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                    {EXPERIENCE.map((t) => {
                        const active = data.experience.includes(t);
                        return (
                            <button
                                type="button"
                                key={t}
                                data-testid={`experience-${t.replace(/\s/g, "-").toLowerCase()}`}
                                onClick={() => toggleExperience(t)}
                                className={`text-xs uppercase tracking-[0.2em] px-3 py-2 border transition-all ${
                                    active
                                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                                        : "text-white/65 border-white/15 hover:border-[#D4AF37]/50"
                                }`}
                            >
                                {t}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                    Why do you want to join the Young Mogul ecosystem?{" "}
                    <span className="text-[#D4AF37]">*</span>
                </label>
                <textarea
                    name="why_join"
                    value={data.why_join}
                    onChange={onChange}
                    rows={4}
                    required
                    data-testid="apply-why"
                    placeholder="Speak your truth..."
                    className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
                />
            </div>

            <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-display uppercase tracking-widest py-4 hover:bg-[#FFF3A1] transition-all disabled:opacity-50"
            >
                {submitting ? "Submitting..." : "Submit Application"}
                <Send size={16} />
            </button>
        </form>
    </div>
);

const SchoolForm = ({ data, onChange, onBack, onSubmit, submitting }) => (
    <div className="p-8 md:p-12">
        <BackButton onClick={onBack} />

        <div className="flex items-center gap-3 mb-2 mt-4">
            <Building2 size={16} className="text-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                School Application
            </span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-[1] text-white">
            Bring YMP To{" "}
            <span className="gold-text-gradient">Your Campus</span>
        </h3>
        <p className="mt-3 text-white/60 text-sm">
            Apply to bring the 9-week Young Mogul Program Accelerator to your
            school.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="apply-form-school">
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="School Name" name="school_name" value={data.school_name} onChange={onChange} required testId="school-name" />
                <Field label="District" name="district" value={data.district} onChange={onChange} testId="school-district" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your Name" name="full_name" value={data.full_name} onChange={onChange} required testId="school-contact" />
                <Field label="Position / Role" name="position" value={data.position} onChange={onChange} testId="school-position" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Email" name="email" type="email" value={data.email} onChange={onChange} required testId="school-email" />
                <Field label="Phone" name="phone" value={data.phone} onChange={onChange} required testId="school-phone" />
                <Field label="City" name="city" value={data.city} onChange={onChange} required testId="school-city" />
            </div>

            <div>
                <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                    Why do you want to bring YMP to your school?{" "}
                    <span className="text-[#D4AF37]">*</span>
                </label>
                <textarea
                    name="message"
                    value={data.message}
                    onChange={onChange}
                    rows={5}
                    required
                    data-testid="school-message"
                    placeholder="Tell us about your students and goals..."
                    className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
                />
            </div>

            <button
                type="submit"
                disabled={submitting}
                data-testid="school-submit"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-display uppercase tracking-widest py-4 hover:bg-[#FFF3A1] transition-all disabled:opacity-50"
            >
                {submitting ? "Submitting..." : "Submit Application"}
                <Send size={16} />
            </button>
        </form>
    </div>
);

const BackButton = ({ onClick }) => (
    <button
        onClick={onClick}
        data-testid="apply-back"
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-[#D4AF37]"
    >
        <ArrowLeft size={12} /> Change Path
    </button>
);

const ResumeUpload = ({ file, uploading, onSelect }) => {
    const inputRef = useRef(null);

    const pick = () => inputRef.current?.click();

    const handleChange = (e) => {
        const f = e.target.files?.[0];
        if (f) onSelect(f);
        // reset so the same file can be re-selected later if cleared
        e.target.value = "";
    };

    const clear = () => onSelect(null);

    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                Resume <span className="text-white/30">(PDF, DOC, DOCX — max 10MB)</span>
            </label>

            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleChange}
                className="hidden"
                data-testid="apply-resume-input"
            />

            {!file && (
                <button
                    type="button"
                    onClick={pick}
                    disabled={uploading}
                    data-testid="apply-resume-pick"
                    className="mt-2 w-full flex items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-[#D4AF37] hover:bg-[#D4AF37]/[0.04] py-6 text-sm text-white/65 hover:text-[#D4AF37] transition-all disabled:opacity-50 disabled:cursor-wait"
                >
                    {uploading ? (
                        <>
                            <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
                            <span className="uppercase tracking-[0.2em] text-xs">Uploading...</span>
                        </>
                    ) : (
                        <>
                            <Upload size={16} className="text-[#D4AF37]" />
                            <span className="uppercase tracking-[0.2em] text-xs">
                                Click to upload your resume
                            </span>
                        </>
                    )}
                </button>
            )}

            {file && (
                <div
                    data-testid="apply-resume-file"
                    className="mt-2 flex items-center justify-between gap-3 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] px-4 py-3"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <FileText size={18} className="text-[#D4AF37] shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm text-white truncate">{file.name}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                                {(file.size / 1024).toFixed(0)} KB · uploaded
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={clear}
                        data-testid="apply-resume-clear"
                        className="text-white/55 hover:text-[#D4AF37] shrink-0"
                        aria-label="Remove file"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, testId, icon: Icon, ...rest }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
            {label}
            {rest.required && <span className="text-[#D4AF37] ml-1">*</span>}
        </label>
        <div className="relative">
            {Icon && (
                <Icon
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37] pointer-events-none"
                />
            )}
            <input
                {...rest}
                data-testid={testId}
                className={`mt-2 w-full bg-black/40 border border-white/10 ${Icon ? "pl-9 pr-4" : "px-4"} py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none`}
            />
        </div>
    </div>
);
